import { useEffect, useId, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { focusReturnTarget, isTopmost, modalStackReducer, t, type ModalLayer } from '@bms2/shared';
import { Icon } from '../Icon';
import { SplitAsButtonsContext } from '../controls/splitAsButtons';
import styles from './modal.module.css';

/**
 * The Modal shell (piece 5, decisions 0070, 0138). One construction of three
 * regions — a Title Bar, a Body and a Footer — over a native `dialog` opened
 * with `showModal()`. Every modal in the application is this element: the two
 * confirmation dialogs of Core primitive components are adopted rather than
 * rebuilt, and the completeness suite fails a second `dialog` anywhere else.
 *
 * What the native element carries, this does not reimplement: the top layer,
 * the backdrop, the focus trap, Escape-to-close, and the inertness of
 * everything behind it. What it adds is the two rules the platform does not
 * give and the region layout:
 *
 *  - a backdrop click never cancels, because a stray click outside a form is
 *    not a decision to discard what was typed into it;
 *  - closing returns focus to exactly the control that opened this layer — by
 *    the `×`, by Cancel and by Escape alike — and on a stack that is a control
 *    inside the layer beneath, never the top of the document.
 *
 * It renders no Button, so the Button's own destructive dialog can be built on
 * it without the two importing each other.
 */

/**
 * The open layers, in the order they opened. It is one store for the whole
 * process rather than a React context, because the top layer is one thing the
 * browser has and not one per subtree: a modal opened from a page and a modal
 * opened from inside another modal land in the same stack, and the layer
 * beneath has to learn that something opened over it.
 */
let openLayers: readonly ModalLayer[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribeToStack(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function stackSnapshot(): readonly ModalLayer[] {
  return openLayers;
}

function pushLayer(layer: ModalLayer): void {
  openLayers = modalStackReducer(openLayers, { type: 'open', layer });
  emit();
}

function popLayer(id: string): void {
  openLayers = modalStackReducer(openLayers, { type: 'close', id });
  emit();
}

export function useModalStack(): readonly ModalLayer[] {
  return useSyncExternalStore(subscribeToStack, stackSnapshot, stackSnapshot);
}

export interface ModalShellProps {
  open: boolean;
  title: string;
  /** The context line under the title: which record this modal is about. */
  subtitle?: string;
  /** The `×`, Cancel and Escape are one outcome, and all three call this. */
  onCancel: () => void;
  /**
   * 🔒 The three ways out held together while the Primary's write is in flight
   * (the KB4 review, 2026-10-01): the `×` and Cancel draw disabled and Escape
   * does nothing, so a window cannot close on a write that carries on unseen.
   */
  held?: boolean;
  /** The Footer's own controls, pinned outside the scrolling Body. */
  footer: ReactNode;
  children: ReactNode;
  /** The destructive confirmation's red header; every ordinary modal is navy. */
  tone?: 'navy' | 'destructive';
  /** `document` is the width of a document and its fields side by side: the Edit Expense window (decision 1185). */
  width?: 'regular' | 'wide' | 'document';
  /**
   * 🔨 `tall` takes the whole height the screen allows and lifts the Body's own
   * cap, and two modals ask for it: the viewer a rendered sheet is read in
   * (decision 0563) and the Edit Expense window, whose left half is the
   * receipt (decision 1185). Every other modal is `content`, so the Body stops
   * short of filling a screen that has room to spare.
   */
  height?: 'content' | 'tall';
  /** The id of the control that opened this layer, for the focus return. */
  openerId?: string | null;
  /** Which modal this is, for the harness and the acceptance suites. */
  dataModal: string;
  /**
   * 🔒 Decision 2641: a press on the backdrop closes the window. Only a window that writes nothing asks for it, through
   * `Modal`'s close-only Footer; every window with a Cancel ignores the backdrop, as piece 5 holds.
   */
  backdropCloses?: boolean;
}

export function ModalShell({ open, title, subtitle, onCancel, held = false, footer, children, tone = 'navy', width = 'regular', height = 'content', openerId = null, dataModal, backdropCloses = false }: ModalShellProps) {
  const id = useId();
  const ref = useRef<HTMLDialogElement>(null);
  const stack = useModalStack();
  const layer = useMemo<ModalLayer>(() => ({ id, openerId }), [id, openerId]);

  // The layer joins the stack while it is open and leaves on close or unmount,
  // so the layer beneath sees that something opened over it.
  useEffect(() => {
    if (!open) return undefined;
    pushLayer(layer);
    return () => popLayer(layer.id);
  }, [open, layer]);

  /**
   * 🔒 The control that held focus when this layer opened (decision 3063). A
   * caller that hands no `openerId` still gets its focus back: the shell reads
   * the opener itself, before `showModal()` moves focus into the dialog.
   */
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body && !el.contains(active) ? active : null;
      el.showModal();
    } else if (!open && el.open) el.close();
  }, [open]);

  // The focus return is written down rather than left to the browser, because
  // the three ways out are one outcome and all three land here.
  const wasOpen = useRef(false);
  const handBack = useRef<() => void>(() => undefined);
  // The newest stack and layer, read when this layer closes: the return runs when this layer closes, not when a sibling opens.
  useEffect(() => {
    handBack.current = () => {
      const target = focusReturnTarget(modalStackReducer(stack, { type: 'open', layer }), id);
      const named = target ? document.getElementById(target) : null;
      const control = named instanceof HTMLElement ? named : opener.current;
      if (control && control.isConnected) control.focus();
    };
  });
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    handBack.current();
  }, [open]);
  /**
   * 🔒 A window its caller unmounts on close, the pattern most windows take,
   * hands focus back as it goes (decision 3063). 💰 Before, the close effect
   * never ran for those, so focus fell to the top of the document.
   */
  useEffect(() => () => {
    if (wasOpen.current) handBack.current();
  }, []);

  return (
    <dialog
      ref={ref}
      className={`${styles.shell} ${width === 'wide' ? styles.wide : width === 'document' ? styles.document : ''} ${height === 'tall' ? styles.tall : ''}`}
      aria-labelledby={`${id}-title`}
      data-modal={dataModal}
      data-topmost={isTopmost(stack, id) ? 'true' : 'false'}
      // Escape reaches the top layer only. The browser dispatches `cancel` on
      // that dialog alone, but React routes the event through its own tree and
      // a stacked modal is a DOM descendant of the one beneath it: without the
      // target check, one Escape closes every layer under the top one too.
      // The default is prevented so the close goes through one path.
      onCancel={(event) => {
        event.preventDefault();
        if (event.target !== event.currentTarget) return;
        event.stopPropagation();
        if (!held) onCancel();
      }}
      // 🔒 Decision 2641: a press on the backdrop lands on the dialog itself, outside its card; a press inside the card never closes it.
      onClick={
        backdropCloses
          ? (event) => {
              if (event.target !== event.currentTarget || held) return;
              const card = event.currentTarget.getBoundingClientRect();
              const outside = event.clientX < card.left || event.clientX > card.right || event.clientY < card.top || event.clientY > card.bottom;
              if (outside) onCancel();
            }
          : undefined
      }
    >
      {/* 🔨 A window keeps its own split Primary whole, even opened from a phone footer that lays splits out (decision 3052). */}
      <SplitAsButtonsContext.Provider value={false}>
        <div className={`${styles.titleBar} ${tone === 'destructive' ? styles.destructiveTitleBar : ''}`}>
          <h2 className={styles.title} id={`${id}-title`}>
            {title}
            {subtitle ? <span className={styles.subtitle}>{subtitle}</span> : null}
          </h2>
          <button type="button" className={styles.close} onClick={onCancel} disabled={held} aria-label={t('modal.close')} data-modal-exit="close">
            <Icon meaning="closeModal" size="inline" />
          </button>
        </div>
        <div className={styles.body} data-modal-body="true">
          {children}
        </div>
        <div className={styles.footer} data-modal-footer="true">
          {footer}
        </div>
      </SplitAsButtonsContext.Provider>
    </dialog>
  );
}

/** The Footer's Cancel, in the shell rather than in each modal that has one. */
export function ModalCancel({ label, onCancel, disabled = false }: { label: string; onCancel: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={styles.cancel} onClick={onCancel} disabled={disabled} data-modal-exit="cancel">
      {label}
    </button>
  );
}

/**
 * A Body field with its label, and the red asterisk where it is required.
 * 🔒 The label is a node, so a field naming a figure carries the Money
 * component inside it rather than a formatted string (decision 0717).
 */
export function ModalField({ label, required = false, children }: { label: ReactNode; required?: boolean; children: ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={`${styles.fieldLabel} ${required ? styles.required : ''}`} data-required={required ? 'true' : 'false'}>
        {label}
      </span>
      {children}
    </div>
  );
}
